import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Calendar, CheckCircle, 
  Loader2, ArrowRight, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_BASE_URL = 'https://calendly.com/motivya/30min';

// Build Calendly URL with pre-filled data
// Calendly prefill parameters: name, email, guests, a1 (first custom question)
// For phone, we need to use the phone number field directly if configured
const buildCalendlyUrl = (formData) => {
  const params = new URLSearchParams();
  params.append('name', `${formData.first_name} ${formData.last_name}`);
  params.append('email', formData.email);
  // Note: Calendly's phone prefill requires the event type to have phone as a required field
  // The phone number will be passed but may need manual entry if not configured
  return `${CALENDLY_BASE_URL}?${params.toString()}`;
};

export default function SimpleCoachForm({ onSuccess, t }) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return (
      formData.first_name.trim() &&
      formData.last_name.trim() &&
      formData.email.trim() &&
      formData.phone.trim()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      toast.error(t?.('coach.fillAllFields') || 'Veuillez remplir tous les champs');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t?.('coach.invalidEmail') || 'Adresse email invalide');
      return;
    }

    setLoading(true);
    try {
      // Save lead to backend
      const response = await fetch(`${API_URL}/api/coach-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          source: 'become-coach-page'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'envoi');
      }

      // Build Calendly URL with pre-filled data
      const url = buildCalendlyUrl(formData);
      setCalendlyUrl(url);
      
      setSubmitted(true);
      toast.success(t?.('coach.leadSaved') || 'Informations enregistrées !');
      
      // Open Calendly with pre-filled data
      setTimeout(() => {
        window.open(url, '_blank');
      }, 500);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openCalendly = () => {
    window.open(calendlyUrl || buildCalendlyUrl(formData), '_blank');
  };

  // Success state after submission
  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="text-2xl font-bold mb-3">
          {t?.('coach.thankYou') || 'Merci !'}
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          {t?.('coach.bookCallNow') || 'Réservez maintenant votre appel de validation avec notre équipe.'}
        </p>
        <Button 
          onClick={openCalendly}
          className="btn-primary text-lg px-8 py-6"
          data-testid="open-calendly-btn"
        >
          <Calendar className="w-5 h-5 mr-2" />
          {t?.('coach.bookMyCall') || 'Réserver mon appel'}
        </Button>
        <p className="text-xs text-muted-foreground mt-4">
          {t?.('coach.calendlyNote') || 'Vous serez redirigé vers Calendly'}
        </p>
      </motion.div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-3">
          {t?.('coach.joinCommunity') || 'Rejoignez la communauté'}
        </h2>
        <p className="text-muted-foreground">
          {t?.('coach.in30Seconds') || 'En 30 secondes, réservez votre appel de validation'}
        </p>
      </motion.div>

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onSubmit={handleSubmit}
        className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-6 md:p-8 space-y-5"
        data-testid="simple-coach-form"
      >
        {/* Name fields - side by side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              {t?.('coach.firstName') || 'Prénom'} *
            </label>
            <Input
              type="text"
              placeholder="Jean"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              className="input-modern"
              data-testid="input-first-name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              {t?.('coach.lastName') || 'Nom'} *
            </label>
            <Input
              type="text"
              placeholder="Dupont"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              className="input-modern"
              data-testid="input-last-name"
              required
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-2">
            {t?.('coach.email') || 'Email'} *
          </label>
          <Input
            type="email"
            placeholder="jean.dupont@email.com"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="input-modern"
            data-testid="input-email"
            required
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium mb-2">
            {t?.('coach.phone') || 'Téléphone'} *
          </label>
          <Input
            type="tel"
            placeholder="+32 4XX XX XX XX"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="input-modern"
            data-testid="input-phone"
            required
          />
        </div>

        {/* Submit Button */}
        <Button 
          type="submit"
          className="btn-primary w-full text-lg py-6 mt-6"
          disabled={loading || !isFormValid()}
          data-testid="submit-coach-form"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Calendar className="w-5 h-5 mr-2" />
              {t?.('coach.bookValidationCall') || 'Réserver mon appel de validation'}
            </>
          )}
        </Button>

        {/* Trust indicators */}
        <div className="pt-4 space-y-3">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
            <span>{t?.('coach.call10min') || 'Appel de 10 min avec notre équipe'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Shield className="w-4 h-4 text-green-400 flex-shrink-0" />
            <span>{t?.('coach.profileValidation') || 'Validation de votre profil coach'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <ArrowRight className="w-4 h-4 text-green-400 flex-shrink-0" />
            <span>{t?.('coach.fullAccess') || 'Accès à votre espace coach complet'}</span>
          </div>
        </div>
      </motion.form>

      {/* Already have an account? */}
      <p className="text-center text-sm text-muted-foreground mt-6">
        {t?.('coach.alreadyAccount') || 'Déjà inscrit ?'}{' '}
        <a href="/login" className="text-primary hover:underline">
          {t?.('coach.loginHere') || 'Connectez-vous'}
        </a>
      </p>
    </div>
  );
}
